import { Error } from '../../icons'
import {
  FieldValues,
  useForm,
  UseFormRegister,
  Validate,
} from 'react-hook-form'
import {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  useEffect,
  useState,
} from 'react'
import moment from 'moment'
import { FaExclamationCircle } from 'react-icons/fa'

interface BasicInputFieldProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  className?: string
}

export const BasicInputField = ({
  className,
  label,
  ...props
}: BasicInputFieldProps) => {
  return (
    <div className={`relative ${className}`}>
      <input
        className={`${className} h-full peer block w-full appearance-none border-0 shadow-[0px_0.5px_2px_0px_rgba(0,0,0,0.25)] rounded-lg bg-white px-3.5 py-[1px] pt-5 pb-2 text-gray-900 placeholder-transparent focus:outline-none`}
        type='text'
        {...props}
      />
      <label
        htmlFor={props.id}
        id={`${props.id}-label`}
        className={`text-xs md:text-[12px] absolute left-4 top-4 transform -translate-y-1/2 bg-white transition-all duration-200 ease-in-out
peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
peer-focus:top-4 peer-focus:text-[12px]`}
      >
        {label}
      </label>
    </div>
  )
}

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  errors?: any
  label: string
  errorMessage?: string
  register?: UseFormRegister<FieldValues>
  required?: boolean
  innerClassName?: string
}

export const MultiLineField = ({
  register,
  required,
  label,
  errorMessage,
  className,
  innerClassName,
  errors,
  ...props
}: Props) => {
  return (
    <>
      <div className={`relative ${className}`}>
        <input
          {...(register &&
            register(props.id, {
              required: required,
            }))}
          type='text'
          className={`${innerClassName} h-full peer block w-full appearance-none border-2 rounded-lg bg-white pt-10 px-3.5 py-32 text-gray-900 placeholder-transparent focus:outline-none ${
            errors.message
              ? 'border-[#DE3333]'
              : 'border-gray-300 focus:border-teal-500'
          }`}
          aria-labelledby={`${props.id}-label`}
          aria-invalid={errors?.[props.id] ? 'true' : 'false'}
          {...props}
        />
        <label
          htmlFor={props.id}
          id={`${props.id}-label`}
          className={`text-xs md:text-[12px] absolute left-4 top-4 bg-white transition-all duration-200 ease-in-out
    peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
    peer-focus:top-2 peer-focus:text-[12px] ${
      errors.message
        ? 'peer-focus:text-[#DE3333] peer-placeholder-shown:text-[#DE3333] text-[#DE3333] peer-placeholder-shown:top-0'
        : 'peer-focus:text-teal-600'
    }`}
        >
          {label}
        </label>
      </div>
      {errors?.[props.id]?.type && (
        <div className='flex justify-self-start items-center pt-1 gap-y-4'>
          <Error width='18px' height='18px' />
          <span className='text-sm text-[#DE3333] font-normal'>
            {errorMessage}
          </span>
        </div>
      )}
    </>
  )
}

interface InputGroupProps extends InputHTMLAttributes<HTMLInputElement> {
  Icon: any
  className?: string
  reverse?: boolean
  label?: string
}

export const InputGroupField = ({
  Icon,
  reverse,
  className,
  ...props
}: InputGroupProps) => {
  return (
    <>
      <div className={`relative ${className}`}>
        {Icon && !reverse && (
          <div className='absolute inset-y-0 start-0 flex items-center  pointer-events-none'>
            <Icon />
          </div>
        )}

        <input
          type='text'
          id='voice-search'
          className={` border border-gray-300 text-black text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full h-full  p-2.5`}
          {...props}
          required
        />
        {Icon && reverse && (
          <button
            type='button'
            className='absolute inset-y-0 end-0 flex items-center pe-3'
          >
            <Icon size={16} color={'#122D5C'} />
          </button>
        )}
      </div>
    </>
  )
}

interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  className?: string
  errors?: any
  setError?: any
  errorMessage?: string
  register?: UseFormRegister<FieldValues>
  regexPattern?: RegExp
  required?: boolean
  validate?: () =>
    | Validate<any, FieldValues>
    | Record<string, Validate<any, FieldValues>>
  password?: string
  repeatPassword?: string
  biggerInput?: boolean
  readOnly?: boolean
  onClick?: () => void
  icon?: ReactNode
  labelSize?: string
  innerClassName?: string
  extraLabel?: string
}

export const InputField = ({
  register,
  required,
  validate,
  regexPattern,
  label,
  className,
  errors,
  setError,
  errorMessage,
  innerClassName,
  labelSize,
  ...props
}: InputFieldProps & { extraLabel?: string }) => {
  const [isOnFocus, setIsOnFocus] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState<string>('')

  return (
    <div
      className={`w-full flex flex-col justify-self-start item-center ${
        props.readOnly && 'cursor-pointer'
      }`}
    >
      <div
        className={`relative ${className}`}
        onClick={props.onClick && props.onClick}
      >
        {props.icon && (
          <div className='absolute inset-y-0 right-4 flex items-center'>
            {props.icon}
          </div>
        )}
        <input
          {...register(props.id, {
            required: required,
            pattern: regexPattern,
            validate:
              props.id === 'repeatPassword'
                ? (value) =>
                    value === props.password || 'Passwords do not match'
                : undefined,
          })}
          type='text'
          onChange={(e) => {
            setInputValue(e.target.value)
            register(props.id).onChange(e)
          }}
          onFocus={() => setIsOnFocus(true)}
          onBlur={(e) => {
            setIsOnFocus(false)
            register(props.id).onBlur(e)
          }}
          className={`${innerClassName} ${
            props.disabled && '!bg-[#F4F4F4] !text-[#A0ABBE]'
          } h-full peer block shadow-[0px_0.4px_2px_0px_rgba(0,0,0,0.25)] w-full appearance-none rounded-lg bg-white p-[4px_10px] pt-[16px] text-gray-900 placeholder-transparent !text-sm focus:outline-none focus:ring-0 ${
            errors?.[props.id]
              ? 'border-[#DE3333]'
              : 'border-gray-300 focus-within:border focus-within:border-[#B3EBF9]'
          } ${props.readOnly && 'cursor-pointer'}`}
          aria-labelledby={`${props.id}-label`}
          aria-invalid={errors?.[props.id] ? 'true' : 'false'}
          readOnly={props.readOnly}
          {...props}
        />
        <label
          htmlFor={props.id}
          id={`${props.id}-label`}
          className={`text-xs  absolute left-[10px] top-[13px] transform -translate-y-1/2  transition-all duration-200 ease-in-out
peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-base peer-placeholder-shown:text-[#818285] ${
            props.disabled && '!text-[#A0ABBE]'
          }
peer-focus:top-[10px] peer-focus:text-xs ${
            errors?.[props.id]
              ? '!peer-focus:text-[#DE3333]  !peer-placeholder-shown:text-[#DE3333] !text-[#DE3333]'
              : 'peer-[:not(:placeholder-shown)]:text-[#37A2BA] peer-focus:text-[#37A2BA]'
          }`}
        >
          {props.extraLabel && !props.readOnly && !isOnFocus && !inputValue
            ? props.extraLabel
            : label}
        </label>
      </div>
      {errors?.[props.id]?.type && (
        <div className='flex justify-self-start items-center pt-1 gap-y-4'>
          <Error width='18px' height='18px' />
          <span
            className={`${
              labelSize ? `text-[${labelSize}]` : 'text-sm'
            } text-[#DE3333] font-normal`}
          >
            {errorMessage}
          </span>
        </div>
      )}
    </div>
  )
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  records?: any
  className?: string
  label?: string
  errors?: any
  errorMessage?: string
  register?: UseFormRegister<FieldValues>
  regexPattern?: RegExp
  required?: boolean
  defaultValue?: any
  selectClassName?: string
  onSelectionChange?: any
  value?: string
  placeholder?: string
  mobileIcon?: React.ReactNode
}

export const SelectField = ({
  className,
  records,
  label,
  errorMessage,
  errors,
  register,
  selectClassName = '',
  required,
  defaultValue,
  onSelectionChange,
  value = '',
  placeholder,
  onChange,
  mobileIcon,
  ...props
}: SelectFieldProps) => {
  const [showDropdown, setShowDropdown] = useState<boolean>(false)
  const [selected, setSelected] = useState<string>(value || label)

  useEffect(() => {
    setSelected(value || label)
  }, [value, label])

  const onSelected = (option: any) => {
    setSelected(option.label)
    setShowDropdown(!showDropdown)
    if (onSelectionChange) {
      onSelectionChange(option.label)
    }
    if (onChange) {
      onChange({
        target: { value: option.value },
      } as React.ChangeEvent<HTMLSelectElement>)
    }
  }

  const { setError } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  return (
    <div className={`relative ${className}`}>
      <button
        {...(register &&
          register(props.id, {
            required: required,
            // @ts-ignore
            validate: (() => {
              if (selected === label) {
                setError(props.id, {
                  type: 'manual',
                  message: 'The password doesn’t match',
                })
              }
            })(),
          }))}
        value={selected === label ? '' : selected}
        onClick={() => setShowDropdown(!showDropdown)}
        id='dropdownDefaultButton'
        data-dropdown-toggle='dropdown'
        className={`w-full h-[45px] appearance-none
        ${
          mobileIcon
            ? 'md:border-[0.8px] md:shadow-[0px_0.4px_2px_0px_rgba(0,0,0,0.25)]'
            : 'border-[0.8px] shadow-[0px_0.4px_2px_0px_rgba(0,0,0,0.25)]'
        }
        ${
          errors?.[props.id]
            ? 'border-[#DE3333] !text-[10px] font-normal !text-[#DE3333]'
            : 'border-[#F4F4F4]'
        }
        bg-[#FFF] hover:bg-[#EBFBFF]
        ${selected !== label ? 'text-[#37A2BA]' : 'text-[#00323D]'}
        font-medium focus:outline-none rounded-[8px] text-base ${
          mobileIcon ? 'md:p-[12px_16px]' : 'p-[12px_16px]'
        } inline-flex items-center justify-between ${selectClassName}`}
        type='button'
      >
        {selected === undefined && placeholder}
        {mobileIcon && <span className='inline md:hidden'>{mobileIcon}</span>}
        <span className={`${mobileIcon ? 'hidden md:inline' : ''}`}>
          {records.find((item) => item?.value === selected)?.label || selected}
        </span>
        {!mobileIcon &&
          (showDropdown ? (
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='12'
              height='12'
              viewBox='0 0 12 12'
              fill='none'
            >
              <path d='M6 3L12 9L0 9L6 3Z' fill='#37A2BA' />
            </svg>
          ) : (
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='12'
              height='12'
              viewBox='0 0 12 12'
              fill='none'
            >
              <path d='M6 9L0 3H12L6 9Z' fill='#37A2BA' />
            </svg>
          ))}
      </button>

      {showDropdown && (
        <div
          id={`dropdown`}
          className={`!z-[9999999] shadow-[0px_0.4px_4px_0px_rgba(34,81,143,0.25)] absolute mt-[4px] w-max p-[4px_8px] bg-white divide-y divide-gray-100 rounded-[8px]`}
        >
          <ul
            className='py-2 p-[6px] text-sm text-gray-700 dark:text-gray-200'
            aria-labelledby='dropdownDefaultButton'
          >
            {records.map((option, idx) => (
              <li
                key={option + idx}
                onClick={() => onSelected(option)}
                className='hover:bg-[#EBFBFF]'
              >
                <button
                  className={`block p-[6px] text-[#101828] text-base font-medium 
                    ${
                      selected === option ? 'text-[#37A2BA]' : 'text-[#101828]'
                    } 
                    `}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {errors?.[props.id]?.type && (
        <div className='flex justify-self-start items-center text-[10px] pt-1 gap-y-4'>
          <Error width='18px' height='18px' />
          <span className={`text-[#DE3333] font-normal`}>{errorMessage}</span>
        </div>
      )}
    </div>
  )
}

interface DateInputProps {
  placeholder?: string
  value: string
  setValue: React.Dispatch<React.SetStateAction<string>>
  className?: string
  name?: string
  onFocus?: () => void
}

const stripNonDigits = (input: string) => input.replace(/\D/g, '')
const limitLength = (input: string, max = 8) => input.slice(0, max)
const formatDate = (input: string) => {
  const day = input.slice(0, 2)
  const month = input.slice(2, 4)
  const year = input.slice(4, 8)

  if (input.length <= 2) return day
  if (input.length <= 4) return `${day}/${month}`
  return `${day}/${month}/${year}`
}
const isValidDate = (value: string) =>
  moment(value, 'DD/MM/YYYY', true).isValid()

export const DateInput = ({
  className,
  value,
  setValue,
  onFocus,
  ...props
}: DateInputProps) => {
  const [error, setError] = useState(false)
  const [isSelected, setIsSelected] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatDate(limitLength(stripNonDigits(e.target.value)))
    setValue(formatted)
    setError(false)
  }

  const handleBlur = () => {
    setIsSelected(false)
    if (value && !isValidDate(value)) {
      setError(true)
    }
  }

  const handleFocus = () => {
    setIsSelected(true)
    if (onFocus) onFocus()
  }

  const isFloating = value.length > 0 || error || isSelected

  return (
    <div className={`relative ${className}`}>
      <input
        id={`datepicker-${props.name}-${props.placeholder}`}
        type='text'
        inputMode='numeric'
        placeholder=' '
        value={value}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        maxLength={10}
        className={`peer bg-gray-50 border ${
          error ? 'border-[#DE3333]' : 'border-gray-300'
        } text-gray-900 text-sm rounded-lg focus:ring-[#B3EBF9] focus:border-[#B3EBF9] block w-full px-2.5 pt-5 pb-2 text-left placeholder-transparent caret-[#37A2BA]`}
        {...props}
      />
      <label
        htmlFor={`datepicker-${props.name}-${props.placeholder}`}
        className={`absolute text-sm left-2.5 transition-all duration-200 pointer-events-none
    ${isFloating ? 'top-1 text-sm' : 'top-3.5 text-base'}
    ${
      error ? 'text-[#DE3333]' : isFloating ? 'text-[#37A2BA]' : 'text-gray-400'
    }
  `}
      >
        {props.placeholder}
      </label>

      {error && (
        <div className='flex items-center gap-1 text-[#DE3333] text-[10px] absolute top-full mt-1 left-2.5'>
          <FaExclamationCircle className='w-3.5 h-3.5' />
          <span>Insert a valid date.</span>
        </div>
      )}
    </div>
  )
}

interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
}

export const CheckboxField = ({ label, ...props }: CheckboxProps) => {
  return (
    <div className='flex mt-1'>
      <input
        {...props}
        id={props.id}
        type='checkbox'
        className='appearance-none rounded relative peer shrink-0 w-[16px] h-[16px] shadow-[0.1px_0.5px_4px_0px_rgba(0,0,0,0.20)_inset] bg-[#B8E0DB] checked:bg-[#0C5F71] checked:border-0'
      />
      {label && (
        <label
          htmlFor={props.id}
          className='ms-2 text-sm font-medium text-gray-900 dark:text-gray-300'
        >
          {label}
        </label>
      )}
      <svg
        className='
       absolute
       w-[16px] h-[16px]
       hidden peer-checked:!block'
        xmlns='http://www.w3.org/2000/svg'
        viewBox='0 0 17 17'
        fill='none'
      >
        <path
          d='M13.2768 6.18919C13.5744 5.86375 13.5744 5.33612 13.2768 5.01068C12.9793 4.68524 12.4969 4.68524 12.1993 5.01068L7.5 10.0166C4.80065 7.01068 7.5 10.0166 4.80065 7.01068C4.50311 6.68524 4.0207 6.68524 3.72316 7.01068C3.42561 7.33612 3.42561 7.86375 3.72316 8.18919L6.96125 11.8559C7.10414 12.0121 7.29793 12.0999 7.5 12.0999C7.70207 12.0999 7.89586 12.0121 8.03875 11.8559L13.2768 6.18919Z'
          fill='white'
        />
      </svg>
    </div>
  )
}
